import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from 'layouts/AppLayout';
import Button from 'components/ui/Button';
import Icon from 'components/AppIcon';
import SummaryStatsBar from './components/SummaryStatsBar';
import FilterToolbar from './components/FilterToolbar';
import AssetsTable from './components/AssetsTable';
import { ASSETS as RAW_ASSETS } from '../asset-management/data/assetData';

// Derive next milestone from keyOperationalDates or keyExpiryDates
function deriveNextMilestone(asset) {
  const today = new Date();
  const allDates = [
    ...(asset?.keyOperationalDates || []),
    ...(asset?.keyExpiryDates || []),
  ];
  const upcoming = allDates?.filter(d => new Date(d?.date) > today)?.sort((a, b) => new Date(a?.date) - new Date(b?.date));
  if (!upcoming?.length) return '—';
  const d = new Date(upcoming[0]?.date);
  return d?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// Deployed amounts for construction assets (spec-defined)
const CONSTRUCTION_DEPLOYED = {
  'AST-005': 28, // Porto Digital Hub
  'AST-007': 52, // Lisbon Metro Extension
  'AST-006': 31, // Elbe Biomass Plant (stage is Operational but spec lists it)
};

// Map canonical assetData fields to Portfolio Assets UI fields
const ASSETS = RAW_ASSETS?.map(a => ({
  id: a?.id,
  name: a?.name,
  sector: a?.sector,
  region: a?.country,
  stage: a?.stage,
  fund: a?.fund,
  shareholdingPct: a?.shareholdingPct,
  riskScore: a?.riskScore,
  watchlistStatus: a?.watchlistStatus,
  reviewStatus: a?.reviewStatus,
  commitment: a?.fundExposure,
  deployed: a?.stage === 'Operational'
    ? (CONSTRUCTION_DEPLOYED?.[a?.id] !== undefined ? CONSTRUCTION_DEPLOYED?.[a?.id] : a?.fundExposure)
    : (CONSTRUCTION_DEPLOYED?.[a?.id] ?? a?.fundExposure),
  irr: a?.irr,
  moic: a?.multiple,
  esg: a?.esgScore,
  nextMilestone: deriveNextMilestone(a),
}));

const DEFAULT_FILTERS = { stage: 'All', sector: 'All', region: 'All', irrRange: 'All' };

function matchesIRR(irr, range) {
  if (range === 'All') return true;
  if (range === '< 8%') return irr < 8;
  if (range === '8–12%') return irr >= 8 && irr <= 12;
  if (range === '12–16%') return irr > 12 && irr <= 16;
  if (range === '> 16%') return irr > 16;
  return true;
}

const INTELLIGENCE_CHIPS = [
  { label: 'Portfolio Heatmap', icon: 'LayoutGrid', path: '/asset-management/portfolio-heatmap' },
  { label: 'Asset Lifecycle', icon: 'GitBranch', path: '/asset-management/asset-lifecycle' },
  { label: 'Vintage Analysis', icon: 'TrendingUp', path: '/asset-management/vintage-analysis' },
];

export default function PortfolioAssetsPage() {
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState('investor');
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [searchQuery, setSearchQuery] = useState('');
  const [toast, setToast] = useState(null);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const handleIntelligenceNav = (path) => {
    showToast('Opening Asset Management view');
    setTimeout(() => navigate(path), 400);
  };

  const filtered = useMemo(() => {
    return ASSETS?.filter(a => {
      if (filters?.stage !== 'All' && a?.stage !== filters?.stage) return false;
      if (filters?.sector !== 'All' && a?.sector !== filters?.sector) return false;
      if (filters?.region !== 'All' && a?.region !== filters?.region) return false;
      if (!matchesIRR(a?.irr, filters?.irrRange)) return false;
      if (searchQuery?.trim()) {
        const q = searchQuery?.toLowerCase();
        if (
          !a?.name?.toLowerCase()?.includes(q) &&
          !a?.region?.toLowerCase()?.includes(q) &&
          !a?.sector?.toLowerCase()?.includes(q)
        ) return false;
      }
      return true;
    });
  }, [filters, searchQuery]);

  return (
    <AppLayout userRole={userRole} onRoleChange={setUserRole}>
      {/* Toast */}
      {toast && (
        <div
          className="fixed top-5 right-5 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg border border-border bg-card z-50 max-w-sm"
          style={{ zIndex: 9999 }}
        >
          <Icon name="CheckCircle2" size={16} color="var(--color-success)" />
          <span className="font-caption text-foreground text-sm">{toast}</span>
          <button onClick={() => setToast(null)} className="ml-auto">
            <Icon name="X" size={13} color="var(--color-muted-foreground)" />
          </button>
        </div>
      )}
      <div className="min-h-screen bg-background">
        {/* Page Header */}
        <div
          className="sticky top-0 bg-background border-b border-border px-6 md:px-10 lg:px-12 py-4 flex items-center justify-between gap-4 flex-wrap"
          style={{ zIndex: 'var(--z-nav)' }}
        >
          <div className="pl-10 lg:pl-0">
            <h1 className="font-heading font-semibold text-foreground" style={{ fontSize: '1.375rem', lineHeight: 1.3 }}>
              Portfolio Assets
            </h1>
            <p className="font-caption text-muted-foreground text-sm mt-0.5">
              Sample data — for demonstration purposes only
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" color="var(--color-muted-foreground)" />
              <input
                type="text"
                placeholder="Search assets…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="pl-9 pr-4 py-2 rounded-lg border border-border bg-background text-sm font-caption text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all duration-200 w-52"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              iconName="FileDown"
              iconPosition="left"
              iconSize={15}
              onClick={() => {}}
            >
              Export
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 md:px-10 lg:px-12 py-8 space-y-6">
          {/* Summary Stats */}
          <SummaryStatsBar />

          {/* Filter Toolbar */}
          <FilterToolbar filters={filters} onChange={setFilters} resultCount={filtered?.length} />

          {/* Portfolio Intelligence Banner */}
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-caption text-muted-foreground text-xs uppercase tracking-wide" style={{ letterSpacing: '0.05em' }}>Portfolio Intelligence:</span>
            {INTELLIGENCE_CHIPS?.map(chip => (
              <button
                key={chip?.path}
                onClick={() => handleIntelligenceNav(chip?.path)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-primary text-primary text-xs font-medium hover:bg-primary hover:text-primary-foreground transition-all duration-150"
              >
                <Icon name={chip?.icon} size={13} />
                {chip?.label}
              </button>
            ))}
          </div>

          {/* Assets Table */}
          <AssetsTable assets={filtered} searchQuery={searchQuery} showToast={showToast} />

          {/* Demo disclaimer */}
          <p className="text-center font-caption text-muted-foreground text-xs pb-4">
            All figures are sample data for demonstration purposes only and do not represent actual investment performance.
          </p>
        </div>
      </div>
    </AppLayout>
  );
}
